/* ─── Message Service — DMs via Cloud Functions + Firestore ───────── */

import { httpsCallable } from 'firebase/functions';
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  doc,
  Unsubscribe,
} from 'firebase/firestore';
import { FIREBASE_FUNCTIONS, FIRESTORE_DB } from '../firebaseConfig';
import type { ChatMessage, Conversation, BlockStatus, TypingStatus, MessageRequest } from '../types/social';

// ── Cloud Function refs ──────────────────────────────────────────────

const sendUserChatMessageEurW = httpsCallable(FIREBASE_FUNCTIONS, 'sendUserChatMessageEurW');
const getActiveConversationsEurW = httpsCallable(FIREBASE_FUNCTIONS, 'getActiveConversationsEurW');
const markChatAsSeenEurW = httpsCallable(FIREBASE_FUNCTIONS, 'markChatAsSeenEurW');
const getUnreadConversationCountEurW = httpsCallable(FIREBASE_FUNCTIONS, 'getUnreadConversationCountEurW');
const sendEmojiReactionToMessageEurW = httpsCallable(FIREBASE_FUNCTIONS, 'sendEmojiReactionToMessageEurW');
const updateTypingStatusEurW = httpsCallable(FIREBASE_FUNCTIONS, 'updateTypingStatusEurW');
const clearTypingStatusEurW = httpsCallable(FIREBASE_FUNCTIONS, 'clearTypingStatusEurW');
const blockUserEurW = httpsCallable(FIREBASE_FUNCTIONS, 'blockUserEurW');
const unblockUserEurW = httpsCallable(FIREBASE_FUNCTIONS, 'unblockUserEurW');
const checkIfUserBlockedEurW = httpsCallable(FIREBASE_FUNCTIONS, 'checkIfUserBlockedEurW');
const getMessageRequestsEurW = httpsCallable(FIREBASE_FUNCTIONS, 'getMessageRequestsEurW');
const respondToMessageRequestEurW = httpsCallable(FIREBASE_FUNCTIONS, 'respondToMessageRequestEurW');

// ── Conversation list ────────────────────────────────────────────────

export async function getConversations(
  limitCount = 20,
  startAfter?: string
): Promise<{ conversations: Conversation[]; hasMore: boolean }> {
  const res = await getActiveConversationsEurW({ limit: limitCount, startAfter });
  const data = res.data as any;
  // Map cloud function response shape to frontend Conversation type
  const conversations: Conversation[] = (data.conversations || []).map((c: any) => ({
    chatId: c.chatId,
    recipientId: c.otherParticipant?.id || '',
    // Handle only: the callable stopped returning real names in PPL-427.
    recipientName: c.otherParticipant?.tetherHandle || 'user',
    recipientPhotoURL: c.otherParticipant?.profileImageUrl || undefined,
    lastMessage: c.latestMessage?.text || '',
    lastMessageAt: c.lastMessageAt,
    unread: !!c.isUnread,
    messageCount: c.messageCount,
    status: c.status,
    requestedBy: c.requestedBy ?? null,
  }));
  return {
    conversations,
    hasMore: !!data.hasMore,
  };
}

export async function getUnreadCount(): Promise<number> {
  const res = await getUnreadConversationCountEurW();
  return (res.data as any).count || 0;
}

// ── Messages ─────────────────────────────────────────────────────────

/** Generate deterministic chatId from two UIDs */
export function getChatId(uid1: string, uid2: string): string {
  return [uid1, uid2].sort().join('_');
}

/** Send a DM */
export async function sendMessage(
  text: string,
  recipientId: string,
  chatId: string
): Promise<{ success: boolean; messageId?: string; reason?: string }> {
  const res = await sendUserChatMessageEurW({ text, recipientId, chatId });
  return res.data as any;
}

/** Real-time listener for messages in a chat */
export function subscribeToMessages(
  chatId: string,
  onData: (messages: ChatMessage[]) => void,
  onError?: (err: Error) => void,
  maxMessages = 50
): Unsubscribe {
  const q = query(
    collection(FIRESTORE_DB, 'sharedChats', chatId, 'messages'),
    orderBy('timestamp', 'desc'),
    limit(maxMessages)
  );
  return onSnapshot(
    q,
    (snap) => {
      const msgs: ChatMessage[] = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as ChatMessage[];
      onData(msgs.reverse()); // chronological order
    },
    (err) => onError?.(err)
  );
}

/** Mark a chat as seen */
export async function markChatSeen(chatId: string): Promise<void> {
  await markChatAsSeenEurW({ chatId });
}

// ── Reactions ────────────────────────────────────────────────────────

export async function addReaction(chatId: string, messageId: string, emoji: string): Promise<void> {
  await sendEmojiReactionToMessageEurW({ chatId, messageId, emoji });
}

// ── Typing indicators ────────────────────────────────────────────────

export async function setTyping(chatId: string, recipientId: string): Promise<void> {
  await updateTypingStatusEurW({ chatId, recipientId });
}

export async function clearTyping(chatId: string, recipientId: string): Promise<void> {
  await clearTypingStatusEurW({ chatId, recipientId });
}

/** Subscribe to typing status for a chat */
export function subscribeToTyping(
  chatId: string,
  onData: (status: Record<string, TypingStatus>) => void
): Unsubscribe {
  return onSnapshot(doc(FIRESTORE_DB, 'typingStatus', chatId), (snap) => {
    onData((snap.data() || {}) as Record<string, TypingStatus>);
  });
}

// ── Block ────────────────────────────────────────────────────────────

// ── Message requests (PPL-428/429) ───────────────────────────────────

/** The requests waiting on me, newest first. Handle-only by construction. */
export async function getMessageRequests(): Promise<MessageRequest[]> {
  const res = await getMessageRequestsEurW({});
  const data = res.data as { requests?: MessageRequest[] };
  return data.requests || [];
}

/**
 * Answer one. Idempotent server-side, so a double tap is harmless. Declining
 * hides and mutes the conversation for me alone — the requester is never told,
 * and nothing in the response says otherwise.
 */
export async function respondToMessageRequest(
  chatId: string,
  action: 'accept' | 'decline'
): Promise<{ status: string; changed: boolean }> {
  const res = await respondToMessageRequestEurW({ chatId, action });
  const data = res.data as { status: string; changed: boolean };
  return { status: data.status, changed: !!data.changed };
}

export async function blockUser(userIdToBlock: string): Promise<void> {
  await blockUserEurW({ userIdToBlock });
}

export async function unblockUser(userIdToUnblock: string): Promise<void> {
  await unblockUserEurW({ userIdToUnblock });
}

export async function checkBlockStatus(otherUserId: string): Promise<BlockStatus> {
  const res = await checkIfUserBlockedEurW({ otherUserId });
  return res.data as BlockStatus;
}
